<template>
  <v-container fluid>
    <!-- Page Header -->
    <v-row>
      <v-col cols="12">
        <v-card class="mb-4">
          <v-card-title class="py-3 primary white--text">
            <v-icon left color="white">mdi-magnify</v-icon>
            Advanced Search Results
            <!-- <v-spacer></v-spacer> -->
            <!-- <v-btn
              small
              outlined
              color="white"
              @click="$router.push('/dashboard')"
              class="mr-2"
            >
              <v-icon left small>mdi-view-dashboard</v-icon>
              Dashboard
            </v-btn> -->
          </v-card-title>
          <v-card-text class="py-2">
            <v-row>
              <!-- Search Filters Summary -->
              <v-col cols="12" md="8">
                <div class="d-flex flex-wrap align-center">
                  <strong class="mr-2">Filters:</strong>
                  <v-chip v-if="$route.query.keyword" small class="mr-2 mb-1">
                    Keyword: {{ $route.query.keyword }}
                  </v-chip>
                  <v-chip v-if="$route.query.dept" small class="mr-2 mb-1">
                    Department: {{ getDepartmentName($route.query.dept) }}
                  </v-chip>
                  <v-chip v-if="$route.query.branch" small class="mr-2 mb-1">
                    Branch: {{ getBranchName($route.query.branch) }}
                  </v-chip>
                  <v-chip v-if="$route.query.status" small class="mr-2 mb-1">
                    Status: {{ getStatusName($route.query.status) }}
                  </v-chip>
                  <v-chip v-if="$route.query.insurer" small class="mr-2 mb-1">
                    Insurer: {{ getInsurerName($route.query.insurer) }}
                  </v-chip>
                  <span v-if="!hasAnyFilters" class="grey--text"
                    >No filters applied</span
                  >
                </div>
              </v-col>

              <!-- Search Result Count -->
              <v-col cols="12" md="4" class="text-right">
                <span class="text-subtitle-1">
                  {{ filteredCases.length }} Results Found
                </span>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Loading State -->
    <v-row v-if="loading">
      <v-col cols="12" class="text-center">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
        <div class="mt-3">Loading cases...</div>
      </v-col>
    </v-row>

    <!-- No Results State -->
    <v-row v-else-if="!loading && filteredCases.length === 0">
      <v-col cols="12">
        <v-card class="text-center pa-5">
          <v-icon size="64" color="grey lighten-1"
            >mdi-file-search-outline</v-icon
          >
          <h3 class="mt-4 mb-2">No Cases Found</h3>
          <p class="grey--text">Try adjusting your search filters</p>
          <v-btn color="primary" class="mt-3" @click="$router.back()">
            Return
          </v-btn>
        </v-card>
      </v-col>
    </v-row>

    <!-- Results Table -->
    <template v-else>
      <v-card>
        <v-card-title>
          <v-text-field
            v-model="tableSearch"
            append-icon="mdi-magnify"
            label="Filter results"
            single-line
            hide-details
            outlined
            dense
            class="mx-4"
          ></v-text-field>
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="filteredCases"
          :search="tableSearch"
          :items-per-page="10"
          :footer-props="{ 'items-per-page-options': [10, 20, 50, -1] }"
          class="elevation-1"
          @click:row="(row) => navigateToCase(row.id)"
        >
          <!-- Numbering Column -->
          <template slot="item.numbering" slot-scope="props">
            {{ props.index + 1 }}
          </template>

          <!-- Vehicle No Column -->
          <template v-slot:[`item.vehicleNo`]="{ item }">
            <span v-if="item.vehicleNo">{{
              item.vehicleNo.toUpperCase()
            }}</span>
            <span v-else>TBA</span>
          </template>

          <!-- Case ID Column -->
          <template v-slot:[`item.id`]="{ item }">
            AA
            <span
              v-for="(dept, index) in filterDept(item.refType)"
              :key="index"
            >
              /{{ dept.name }}
              <span
                v-for="(subDept, index) in filterSubDept(item.subRefType)"
                :key="index"
              >
                /{{ subDept.subCode }}
              </span>
            </span>
            /{{ item.id }}/{{ yearOnly(item.dateOfAssign) }}
            <span
              v-for="branch in filterBranches(item.branch)"
              :key="branch.id"
            >
              /{{ branch.brCode }}
            </span>
          </template>

          <!-- Insurer Column -->
          <template v-slot:[`item.insurer`]="{ item }">
            <div v-for="ins in filterIns(item.insurer)" :key="ins.id">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <div
                    class="text-truncate"
                    style="max-width: 100px"
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{ ins.name }}
                  </div>
                </template>
                <span>{{ ins.name }}</span>
              </v-tooltip>
            </div>
          </template>

          <!-- Date Column -->
          <template v-slot:[`item.dateOfAssign`]="{ item }">
            {{ formatDate(item.dateOfAssign) }}
          </template>

          <!-- Days Column -->
          <template v-slot:[`item.days`]="{ item }">
            {{
              dateDiff(moment(item.dateOfAssign).format(), moment().format())
            }}
          </template>

          <!-- Adjuster Column -->
          <template v-slot:[`item.adjuster`]="{ item }">
            <span v-if="item.adjuster">
              <div
                v-for="adjuster in filterUser(item.adjuster)"
                :key="adjuster.id"
              >
                {{ adjuster.username }}
              </div>
            </span>
            <span v-else>TBA</span>
          </template>

          <!-- Status Column -->
          <template v-slot:[`item.fileStatus`]="{ item }">
            <v-tooltip left transition="fab-transition">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-if="item.flag === 1"
                  small
                  color="green lighten-1"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-shield-check
                </v-icon>
                <v-icon
                  v-if="item.flag === 2"
                  small
                  color="orange lighten-1"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-shield-half-full
                </v-icon>
                <v-icon
                  v-if="item.flag === 3"
                  small
                  color="red darken-5"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-shield-alert-outline
                </v-icon>
              </template>
              <span v-if="item.flag === 1">GENUINE</span>
              <span v-if="item.flag === 2">SUSPECTED FRAUD</span>
              <span v-if="item.flag === 3">FRAUD</span>
            </v-tooltip>

            <span
              v-for="allStatus in filterStages(item.fileStatus)"
              :key="allStatus.id"
            >
              <v-chip label outlined block x-small>
                {{ allStatus.name }}
              </v-chip>
            </span>
          </template>
        </v-data-table>
      </v-card>
    </template>
  </v-container>
</template>

<script>
import DeptDataService from "@/services/DeptDataService";
import BranchDataService from "@/services/BranchDataService";
import StagesDataService from "@/services/StageDataService";
import InsuranceDataService from "@/services/InsDataService";
import SubRefTypeDataService from "@/services/SubDeptDataService";
import MemberDataService from "@/services/MemberDataService";
import moment from "moment";
import { mapState, mapGetters } from "vuex";

export default {
  name: "AdvancedSearch",

  data() {
    return {
      loading: true,
      allCases: [],
      filteredCases: [],
      refType: [], // departments
      branch: [], // branches
      stages: [], // statuses
      insurers: [],
      subRefTypes: [], // sub reference types
      members: [], // members for adjuster display
      tableSearch: "",
      headers: [
        {
          text: "NO.",
          align: "start",
          sortable: false,
          value: "numbering",
        },
        {
          text: "VEHICLE NO.",
          align: "center",
          value: "vehicleNo",
          sortable: false,
        },
        {
          text: "AA REF",
          align: "start",
          value: "id",
          sortable: false,
        },
        {
          text: "INSURER",
          align: "start",
          value: "insurer",
          sortable: false,
        },
        {
          text: "DOA",
          align: "start",
          sortable: false,
          value: "dateOfAssign",
        },
        {
          text: "DAYS",
          align: "start",
          sortable: false,
          value: "days",
        },
        {
          text: "ADJUSTER",
          align: "start",
          sortable: false,
          value: "adjuster",
        },
        {
          text: "STATUS",
          align: "center",
          value: "fileStatus",
          sortable: false,
        },
      ],
    };
  },

  computed: {
    ...mapState({
      storeLoading: (state) => state.casefiles.isLoading,
      allCasefiles: (state) => state.casefiles.casefiles,
    }),
    ...mapGetters("casefiles", ["getFilteredCasefiles"]),

    userFilteredCasefiles() {
      const user = this.$store.state.auth.user;
      if (!user) return [];

      return this.$store.getters["casefiles/getFilteredCasefiles"](
        user.roles,
        user.dept,
        user.branches,
        user.id
      );
    },

    departmentsForFilter() {
      if (!this.refType || this.refType.length === 0) return [];

      const user = this.$store.state.auth.user;
      if (!user) return [];

      if (user.roles.includes("ADMIN")) {
        return [{ id: "all", name: "All Departments" }, ...this.refType];
      }

      const userDepts = Array.isArray(user.dept) ? user.dept : [user.dept];

      const filteredDepts = this.refType.filter((dept) =>
        userDepts.some((userDept) => userDept === dept.id)
      );

      return [{ id: "all", name: "All Departments" }, ...filteredDepts];
    },

    branchesForFilter() {
      if (!this.branch || this.branch.length === 0) return [];

      const user = this.$store.state.auth.user;
      if (!user) return [];

      if (user.roles.includes("ADMIN") || user.roles.includes("MANAGER")) {
        return [{ id: "all", name: "All Branches" }, ...this.branch];
      }

      const userBranches = Array.isArray(user.branches)
        ? user.branches
        : [user.branches];

      const filteredBranches = this.branch.filter((branch) =>
        userBranches.some((userBranch) => userBranch === branch.id)
      );

      return [{ id: "all", name: "All Branches" }, ...filteredBranches];
    },

    hasAnyFilters() {
      return (
        !!this.$route.query.keyword ||
        !!this.$route.query.dept ||
        !!this.$route.query.branch ||
        !!this.$route.query.status ||
        !!this.$route.query.insurer
      );
    },

    searchQuery() {
      return {
        keyword: this.$route.query.keyword || "",
        dept: this.$route.query.dept || null,
        branch: this.$route.query.branch || null,
        status: this.$route.query.status || null,
        insurer: this.$route.query.insurer || null,
      };
    },
  },

  watch: {
    "$route.query": {
      handler() {
        this.applyFilters();
      },
      deep: true,
    },
  },

  methods: {
    async fetchData() {
      this.loading = true;

      try {
        await this.$store.dispatch("casefiles/fetchCasefiles");

        const [
          deptResponse,
          branchResponse,
          stageResponse,
          insurerResponse,
          subRefTypesResponse,
          membersResponse,
        ] = await Promise.all([
          DeptDataService.getAll(),
          BranchDataService.getAll(),
          StagesDataService.getAll(),
          InsuranceDataService.getAll(),
          SubRefTypeDataService.getAll(),
          MemberDataService.getAll(),
        ]);

        this.refType = deptResponse.data;
        this.branch = branchResponse.data;
        this.stages = stageResponse.data;
        this.insurers = insurerResponse.data;
        this.subRefTypes = subRefTypesResponse.data;
        this.members = membersResponse.data;

        this.applyFilters();
      } catch (error) {
        console.error("Error fetching data:", error);
        this.$store.dispatch("alerts/setAlert", {
          text: "Error loading search results",
          type: "error",
        });
        this.filteredCases = [];
      } finally {
        this.loading = false;
      }
    },

    applyFilters() {
      const permittedCasefiles = this.userFilteredCasefiles;
      if (!permittedCasefiles || permittedCasefiles.length === 0) {
        this.filteredCases = [];
        return;
      }

      const { keyword, dept, branch, status, insurer } = this.searchQuery;

      this.filteredCases = permittedCasefiles.filter((item) => {
        if (dept && dept !== "all") {
          const itemRefType = String(item.refType);
          const queryDept = String(dept);
          if (itemRefType !== queryDept) {
            return false;
          }
        }

        if (branch && branch !== "all") {
          const itemBranch = String(item.branch);
          const queryBranch = String(branch);
          if (itemBranch !== queryBranch) {
            return false;
          }
        }

        if (status && status !== "all" && item.fileStatus !== status) {
          return false;
        }

        if (insurer) {
          const itemInsurer = String(item.insurer);
          const queryInsurer = String(insurer);
          if (itemInsurer !== queryInsurer) {
            return false;
          }
        }

        if (keyword) {
          const keywordLower = keyword.toLowerCase();
          const searchableFields = [
            item.vehicleNo,
            item.claimNo,
            item.personInvolved,
            item.insComment,
            String(item.id),
          ].filter((field) => field != null);

          return searchableFields.some((field) =>
            String(field).toLowerCase().includes(keywordLower)
          );
        }

        return true;
      });
    },

    moment(date) {
      return moment(date);
    },

    yearOnly(value) {
      return moment(value).format("YY");
    },

    dateDiff(startDateString, endDateString) {
      let start = moment(startDateString);
      let end = moment(endDateString);
      let duration = moment.duration(end.diff(start));
      return Math.floor(duration.asDays());
    },

    filterDept(item) {
      return this.refType.filter((dept) => dept.id === item);
    },

    filterSubDept(item) {
      return this.subRefTypes?.filter((subDept) => subDept.id === item) || [];
    },

    filterBranches(item) {
      return this.branch.filter((branch) => branch.id === item);
    },

    filterIns(item) {
      return this.insurers.filter((ins) => ins.id === item);
    },

    filterUser(item) {
      return this.members?.filter((member) => member.id === item) || [];
    },

    filterStages(item) {
      return this.stages.filter((stage) => stage.stageCode === item);
    },

    getDepartmentName(id) {
      if (!id || id === "all") return "All Departments";
      const dept = this.refType.find(
        (d) => d.id === parseInt(id) || d.id === id
      );
      return dept ? dept.name : `Dept ${id}`;
    },

    getBranchName(id) {
      if (!id || id === "all") return "All Branches";
      const br = this.branch.find((b) => b.id === parseInt(id) || b.id === id);
      return br ? br.name : `Branch ${id}`;
    },

    getStatusName(code) {
      if (!code || code === "all") return "All Statuses";
      const stage = this.stages.find((s) => s.stageCode === code);
      return stage ? stage.name : code;
    },

    getStatusColor(status) {
      const colors = {
        NEW: "green",
        PEND: "amber",
        INVE: "blue",
        PEDI: "purple",
        EDAC: "indigo",
        EDI: "pink",
        PAPP: "cyan",
        HOLD: "orange",
        FOIN: "teal",
        DISP: "light-blue",
        CLO: "grey",
        CANC: "red",
      };

      return colors[status] || "grey";
    },

    formatDate(date) {
      return moment(date).format("DD/MM/YYYY");
    },

    navigateToCase(id) {
      this.$router.push({
        name: "vuexFile",
        params: { id },
      });
    },

    getFormattedCaseNumber(item) {
      if (!item) return "N/A";

      const dept = this.refType.find((d) => d.id === item.refType);
      const br = this.branch.find((b) => b.id === item.branch);

      let result = `AA-${item.id}`;

      if (dept) {
        result = `AA/${dept.name}/${item.id}`;
      }

      if (br && br.brCode) {
        result += `/${br.brCode}`;
      }

      return result;
    },

    getInsurerName(id) {
      if (!id) return "N/A";
      const insurer = this.insurers.find((ins) => ins.id === id);
      return insurer ? insurer.name : `Insurer ${id}`;
    },
  },

  mounted() {
    this.fetchData();
  },
};
</script>

<style scoped>
.v-chip {
  font-weight: 500;
}
.v-data-table >>> tbody tr {
  cursor: pointer;
}
</style>
